/**
 * Iter 222 · Daily Care Usage Analytics
 * Feature-usage matrix + time-of-day heatmap + conversion-signal panel.
 */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { ChartLine, Fire, Target, ArrowClockwise, Users } from "@phosphor-icons/react";

export default function DailyCareAnalytics() {
  const [tab, setTab] = useState("matrix");
  const [matrix, setMatrix] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [signals, setSignals] = useState(null);
  const [cohort, setCohort] = useState(null);

  const loadAll = async () => {
    try {
      const [m, h, s, c] = await Promise.all([
        api.get("/analytics/daily-care/matrix"),
        api.get("/analytics/daily-care/time-heatmap"),
        api.get("/analytics/daily-care/conversion-signals"),
        api.get("/analytics/daily-care/cohort-explorer"),
      ]);
      setMatrix(m.data); setHeatmap(h.data);
      setSignals(s.data); setCohort(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <AppShell title="Daily Care Analytics" subtitle="Tracker usage · Conversion signals"
      actions={
        <button onClick={loadAll} data-testid="dc-refresh"
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-[#E5E2D9] bg-white">
          <ArrowClockwise size={14}/> Refresh
        </button>
      }>
      <div className="space-y-4" data-testid="daily-care-analytics-root">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="DAU (all trackers)" value={matrix?.totals?.dau ?? "—"} bg="#DBEAFE" c="#1D4ED8"/>
          <Tile label="MAU (all trackers)" value={matrix?.totals?.mau ?? "—"} bg="#D1FAE5" c="#065F46"/>
          <Tile label="Features tracked" value={matrix?.totals?.features_tracked ?? "—"} bg="#FEF3C7" c="#B45309"/>
          <Tile label="Conversion leads" value={signals?.total_leads ?? "—"} bg="#FEE2E2" c="#B91C1C"/>
        </div>

        <div className="flex items-center gap-2 border-b border-[#E5E2D9]">
          {[
            {k:"matrix",  l:"Feature Matrix", i: ChartLine},
            {k:"time",    l:"Time Heatmap",   i: Fire},
            {k:"signals", l:"Conversion Signals", i: Target},
            {k:"cohort",  l:"Cohort Explorer",   i: Users},
          ].map(t => {
            const Icon = t.i;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`dc-tab-${t.k}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 ${
                  tab === t.k ? "border-[#4338CA] text-[#4338CA]" : "border-transparent text-[#5C5A55]"
                }`}>
                <Icon size={14} weight={tab===t.k?"fill":"regular"}/>
                {t.l}
              </button>
            );
          })}
        </div>

        {tab === "matrix" && matrix && (
          <div className="bg-white rounded-lg border border-[#E5E2D9] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F3EE] text-[#5C5A55] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Feature</th>
                  <th className="text-right px-4 py-3">D1</th>
                  <th className="text-right px-4 py-3">D7</th>
                  <th className="text-right px-4 py-3">D30</th>
                  <th className="text-right px-4 py-3">D90</th>
                  <th className="text-right px-4 py-3">Events (30d)</th>
                  <th className="text-right px-4 py-3">Avg / user</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0EDE4]">
                {matrix.features.map((r, i) => (
                  <tr key={i} data-testid={`matrix-row-${i}`}>
                    <td className="px-4 py-3 font-medium">{r.feature}</td>
                    <td className="px-4 py-3 text-right">{r.unique_1d}</td>
                    <td className="px-4 py-3 text-right">{r.unique_7d}</td>
                    <td className="px-4 py-3 text-right font-medium">{r.unique_30d}</td>
                    <td className="px-4 py-3 text-right">{r.unique_90d}</td>
                    <td className="px-4 py-3 text-right text-[#5C5A55]">{r.events_30d}</td>
                    <td className="px-4 py-3 text-right">{r.avg_events_per_user}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "time" && heatmap && (
          <div className="bg-white rounded-lg border border-[#E5E2D9] p-4 overflow-x-auto">
            <div className="text-xs text-[#8A8780] mb-3">Weekday × hour · darker = more tracker events</div>
            <div className="inline-block">
              <div className="flex ml-8 mb-1">
                {Array.from({length:24}).map((_,h) => (
                  <div key={h} className="w-6 h-6 text-[9px] text-center text-[#8A8780]">{h}</div>
                ))}
              </div>
              {heatmap.heatmap.map((row, dow) => (
                <div key={dow} className="flex items-center">
                  <div className="w-8 text-[10px] uppercase text-[#5C5A55]">{heatmap.weekdays[dow]}</div>
                  {row.map((v, h) => {
                    const max = Math.max(1, ...heatmap.heatmap.flat());
                    const intensity = v / max;
                    return (
                      <div key={h} className="w-6 h-6 border border-white"
                        title={`${heatmap.weekdays[dow]} ${h}:00 → ${v} events`}
                        style={{background: `rgba(67,56,202,${0.1 + intensity * 0.85})`}}/>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "signals" && signals && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {signals.signals.map(s => (
              <div key={s.code} data-testid={`signal-card-${s.code}`}
                    className="p-4 bg-white rounded-lg border border-[#E5E2D9]">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#8A8780]">Signal</div>
                    <div className="font-medium text-[#1A1A1A]">{s.title}</div>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[11px] bg-[#FEE2E2] text-[#B91C1C]">{s.count} leads</span>
                </div>
                <div className="mt-3 p-3 bg-[#F5F3EE] rounded text-xs">
                  <div className="text-[#8A8780] mb-1">Suggested action:</div>
                  <div className="font-medium">{s.action}</div>
                </div>
                <button className="mt-3 w-full text-xs py-1.5 rounded bg-[#4338CA] text-white hover:bg-[#3730A3]">
                  Enrol into WhatsApp campaign →
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "cohort" && cohort && (
          <div className="bg-white rounded-lg border border-[#E5E2D9] p-4">
            <div className="text-xs text-[#8A8780] mb-3">{cohort.notes}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(cohort.segments || {}).map(([seg, n]) => (
                <div key={seg} className="p-4 rounded-md bg-[#F5F3EE]">
                  <div className="text-xs uppercase tracking-wider text-[#8A8780]">{seg}</div>
                  <div className="text-2xl font-heading text-[#4338CA] mt-1">{n}</div>
                  <div className="text-[11px] text-[#8A8780] mt-1">Active in last 30 days</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Tile({ label, value, bg, c }) {
  return (
    <div className="p-4 bg-white rounded-lg border border-[#E5E2D9]">
      <div className="w-8 h-8 rounded-md mb-2 flex items-center justify-center" style={{background: bg}}>
        <ChartLine size={16} color={c} weight="fill"/>
      </div>
      <div className="text-[24px] font-heading font-semibold" style={{color: c}}>{value}</div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mt-1">{label}</div>
    </div>
  );
}
